import { Controller } from "@hotwired/stimulus"

// Hours + minutes pair backing a hidden HH:MM field.
//
// Two-way, for the same reason as break_time_minutes_controller: an outside
// write lands on the hidden field (break_rule_controller raising the Pause to
// the statutory minimum writes the field that submits), and without the return
// leg the two number inputs keep showing the value the employee typed while the
// entry books a different one.
export default class extends Controller {
  static targets = ["hours", "minutes", "hidden"]
  static values = { step: { type: Number, default: 1 } }

  connect() {
    this._applyHidden = () => this.readHidden()
    this._focusIn = () => {
      this._focused = true
    }
    this._focusOut = () => {
      this._focused = false
      this.readHidden()
    }
    this.hiddenTarget.addEventListener("change", this._applyHidden)
    this.editableTargets.forEach((input) => {
      input.addEventListener("focus", this._focusIn)
      input.addEventListener("blur", this._focusOut)
    })
    this.sync()
  }

  disconnect() {
    this.hiddenTarget.removeEventListener("change", this._applyHidden)
    this.editableTargets.forEach((input) => {
      input.removeEventListener("focus", this._focusIn)
      input.removeEventListener("blur", this._focusOut)
    })
  }

  get editableTargets() {
    return [this.hoursTarget, this.minutesTarget]
  }

  sync() {
    const hours = parseInt(this.hoursTarget.value) || 0
    const minutes = parseInt(this.minutesTarget.value) || 0
    this.hiddenTarget.value =
      String(hours).padStart(2, "0") + ":" + String(minutes).padStart(2, "0")
    this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // By value, not by a flag around our own dispatch — see the same guard in
  // break_time_minutes_controller. Neither input carries a name, so the raise
  // break_rule_controller makes lands back here nested inside the very change
  // sync() emitted; a flag bracketing that round trip would swallow it and leave
  // both numbers showing a break the entry is not booked with.
  readHidden() {
    const parts = String(this.hiddenTarget.value || "").split(":")
    if (parts.length < 2) return

    const hours = parseInt(parts[0], 10)
    const minutes = parseInt(parts[1], 10)
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return
    if (hours * 60 + minutes === this.shownMinutes) return
    // Not while the employee is typing — a rewrite under the caret turns the
    // number being entered into a different one. The blur applies it.
    if (this._focused) return

    this.hoursTarget.value = String(hours)
    this.minutesTarget.value = String(minutes)
  }

  get shownMinutes() {
    return (
      (parseInt(this.hoursTarget.value, 10) || 0) * 60 +
      (parseInt(this.minutesTarget.value, 10) || 0)
    )
  }
}
